import { zodResolver } from "@hookform/resolvers/zod";
import { Check } from "lucide-react";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { X } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogClose,
} from "@/components/ui/dialog";
import $ from 'jquery';
import { LoadingSpinner } from "@/components/ui/loading-spinner";

interface IProps {
    openModalForm: boolean;
    closeForm: () => void;
}

interface EmailSubscribeForm {
    email: string;
    firstName: string;
    lastName: string;
    honeypot: string;
    termsConditions: boolean;
}

const EmailSubscribeFormSchema = z.object({
    firstName: z.string().min(3, { message: "First name is required" }),
    lastName: z.string().min(3, { message: "Last name is required" }),
    email: z.string().email().min(3, { message: "Email is required" }),
    honeypot: z.string().optional(),
    termsConditions: z.boolean().refine(value => value === true, { message: "Please agree to the terms and conditions" }),
});

export default function PopUpModalForm({ openModalForm, closeForm }: IProps) {
    const [submissionError, setSubmissionError] = useState<string | null>(null);
    const [hasFormSubmitted, setHasFormSubmitted] = useState(false);
    const [isEmailValid, setIsEmailValid] = useState(false);
    

    const { register, handleSubmit, formState: { errors, isValid }, watch, setValue } = useForm<EmailSubscribeForm>({
        resolver: zodResolver(EmailSubscribeFormSchema),
        mode: 'onChange'
    });
    const emailValue = watch('email', '');

    const validateEmail = (email: string): boolean => {
        if (!email) return false;
        const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!basicEmailRegex.test(email)) return false;
        const checks = [
            email.match(/\.[a-zA-Z]{2,}$/),
            !email.includes('..'),
            !email.match(/^\.|\.$|@\.|\.@/),
            email.match(/@[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]\.[a-zA-Z]{2,}$/)
        ];
        return checks.every(check => check);
    };

    React.useEffect(() => {
        setIsEmailValid(validateEmail(emailValue));
    }, [emailValue]);

    
    const canSubmit = isValid;
    
    const [submitting, setSubmitting] = useState(false);

    const handleFormSubmit = async (data: any) => {
        setSubmissionError(null);
        setSubmitting(true); //loading

        if (data.honeypot) {
            console.log("Bot detected!");
            return;
        }

        // Token check here
        const token = sessionStorage.getItem("x-token");
        if (!token) {
            setSubmissionError("Session token missing. Please refresh the page or log in again.");
            return;
        }

        
        try {
            await $.ajax({
                type: "POST",
                url: `${process.env.REACT_APP_BASE_URL}/_api/cloudflow/v1.0/trigger/e69c6470-6d84-f011-b4cc-00224893fe04`,
                contentType: "application/json",
                processData: false,
                global: false,
                headers: {
                    "__RequestVerificationToken": token
                },
                data: JSON.stringify({ "eventData": JSON.stringify({ "data": JSON.stringify(data) }) }),
                
                success: function () {
                    setHasFormSubmitted(true);
                },
                error: function (xhr, status, error) {
                    setSubmissionError("There was a problem submitting your form. Please try again.");
                }
            });
        } catch (ex) {
            setSubmissionError("There was a problem submitting your form. Please try again.");
        }
        setSubmitting(false); // Stop loading. 
    };

    return (
        <Dialog open={openModalForm} onOpenChange={closeForm}>
            <DialogContent className="max-w-2xl w-full max-h-[90vh] overflow-auto p-0 bg-black" onPointerDownOutside={(e) => e.preventDefault()}>                
                <DialogClose className="absolute right-4 top-4 rounded-full p-2 hover:bg-gray-100 focus:outline-none">
                    <X className="h-5 w-5 text-gray-500" />
                    <span className="sr-only">Close</span>
                </DialogClose>
                {hasFormSubmitted ? (
                    <div className="p-8 sm:p-12 flex flex-col gap-6 sm:gap-8 items-center justify-center">
                        <div className="text-center space-y-4 sm:space-y-6">
                            <div className="flex justify-center">
                                <div className="rounded-full bg-harvey-blue/10 p-6 sm:p-8">
                                    <Check className="h-16 w-16 sm:h-20 sm:w-20 text-harvey-blue" />
                                </div>
                            </div>
                            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-center text-white">Thank You!</h2>
                            <p className="text-center text-sm sm:text-base md:text-lg text-gray-300 px-4 max-w-md mx-auto leading-relaxed">
                                Thank you for signing up for our email marketing. You'll now receive the latest updates, promotions, and exclusive offers.
                            </p>
                            <Link
                                to="/"
                                className="inline-flex items-center text-harvey-red hover:text-harvey-red/80 font-medium underline transition-colors duration-200 text-sm sm:text-base"
                                onClick={closeForm}
                            >
                                Go back to Home
                            </Link>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Form Content */}
                        <div className="p-6 sm:p-8 bg-black">
                            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6 sm:space-y-8">
                                {/* Enhanced Honeypot fields for bot protection */}
                                <div className="absolute -left-[9999px] invisible opacity-0 h-0 overflow-hidden">
                                    <Input {...register("honeypot")} type="text" tabIndex={-1} autoComplete="nope" aria-hidden="true" />
                                </div>

                                {/* Name Fields */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <Label htmlFor="firstName" className="block text-sm sm:text-base font-medium text-white mb-2">
                                            First Name *
                                        </Label>
                                        <Input
                                            id="firstName"
                                            {...register("firstName")}
                                            type="text"
                                            autoComplete="given-name"
                                            placeholder="First name"
                                            className="w-full bg-white text-black text-sm sm:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-transparent h-12 px-4"
                                        />
                                        {errors.firstName && (
                                            <p className="text-red-400 text-sm mt-2">{errors.firstName.message}</p>
                                        )}
                                    </div>
                                    <div className="space-y-3">
                                        <Label htmlFor="lastName" className="block text-sm sm:text-base font-medium text-white mb-2">
                                            Last Name *
                                        </Label>
                                        <Input
                                            id="lastName"
                                            {...register("lastName")}
                                            type="text"
                                            autoComplete="family-name"
                                            placeholder="Last name"
                                            className="w-full bg-white text-black text-sm sm:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-transparent h-12 px-4"
                                        />
                                        {errors.lastName && (
                                            <p className="text-red-400 text-sm mt-2">{errors.lastName.message}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Email Field */}
                                <div className="space-y-3">
                                    <Label htmlFor="email" className="block text-sm sm:text-base font-medium text-white mb-2">
                                        Email Address *
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            id="email"
                                            {...register("email")}
                                            type="email"
                                            autoComplete="email"
                                            placeholder="your.email@company.com"
                                            className={`w-full bg-white text-black text-sm sm:text-base border-gray-300 focus:ring-2 focus:ring-harvey-red focus:border-transparent h-12 px-4 pr-10 ${emailValue && !isEmailValid ? 'border-red-400 focus:ring-red-400' :
                                                emailValue && isEmailValid ? 'border-green-400 focus:ring-green-400' : ''
                                                }`}
                                        />
                                        {emailValue && (
                                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                                {isEmailValid ? (
                                                    <Check className="h-5 w-5 text-green-500" />
                                                ) : (
                                                    <span className="h-5 w-5 text-red-500 flex items-center justify-center text-lg font-bold">×</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {errors.email && (
                                        <p className="text-red-400 text-sm mt-2">{errors.email.message}</p>
                                    )}
                                </div>

                                {/* Terms and Conditions */}
                                <div className="space-y-6 pt-4">
                                    <div className="flex items-start space-x-4">
                                        <Checkbox
                                            id="termsConditions"
                                            checked={watch("termsConditions")}
                                            onCheckedChange={(checked) => {
                                                setValue("termsConditions", checked as boolean, { shouldValidate: true });
                                            }}
                                            className="mt-1 h-6 w-6 bg-white border-gray-300 data-[state=checked]:bg-harvey-red data-[state=checked]:border-harvey-red data-[state=checked]:text-white p-1"
                                        />
                                        <input
                                            {...register("termsConditions")}
                                            type="checkbox"
                                            className="hidden"
                                        />
                                        <Label htmlFor="termsConditions" className="text-sm sm:text-base text-white leading-relaxed cursor-pointer flex-1">
                                            By opting in, I agree to receive marketing emails in accordance with the terms and conditions.
                                        </Label>
                                    </div>
                                    {errors.termsConditions && (
                                        <p className="text-red-400 text-sm mt-2">{errors.termsConditions.message}</p>
                                    )}

                                    {/* Terms Details */}
                                    <div className="bg-gray-800/50 rounded-lg p-4 sm:p-6 space-y-3 border border-gray-700">
                                        <h3 className="font-medium text-sm sm:text-base text-white">Terms and Conditions</h3>
                                        <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                                            By opting in, you elect to receive marketing communications via email about promotions, products, services, events and/or offers from Harvey Norman Commercial Division.
                                            By opting in, you acknowledge to Harvey Norman that you are over the age of eighteen (18) years and consent for your personal information to be included in a database for use in direct marketing by Harvey Norman Commercial Division.
                                            A copy of the privacy policy is available{' '}
                                            <a
                                                href="https://www.harveynormancommercial.nz/privacy-policy/"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-harvey-red hover:text-harvey-red/80 underline font-medium"
                                            >
                                                here
                                            </a>.
                                        </p>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="pt-6">
                                    <Button
                                        type="submit"                                        
                                        disabled={!canSubmit || submitting}
                                        onClick={() => console.log("Button clicked! canSubmit:", canSubmit)}
                                        className="w-full bg-harvey-red hover:bg-harvey-red/90 disabled:bg-harvey-red disabled:cursor-not-allowed text-white font-semibold py-4 text-base sm:text-lg transition-all duration-200 h-14"
                                    >                                        
                                        {submitting ? (
                                            <>
                                                <LoadingSpinner size="sm" message="" fullScreen={false} inline />
                                                <span className="ml-2">Submitting...</span>
                                            </>
                                        ) : (
                                            "Subscribe Now"
                                        )}
                                    </Button>
                                    {submissionError && (
                                        <p className="text-red-400 text-xs text-center mt-2">{submissionError}</p>
                                    )}
                                    {(!isEmailValid && emailValue) || !watch("termsConditions") ? (
                                        <div className="mt-3 space-y-1">
                                            {!isEmailValid && emailValue && (
                                                <p className="text-red-400 text-xs text-center">
                                                    Please enter a valid email address
                                                </p>
                                            )}
                                            {!watch("termsConditions") && (
                                                <p className="text-red-400 text-xs text-center">
                                                    Please accept the terms and conditions
                                                </p>
                                            )}
                                        </div>
                                    ) : null}
                                </div>
                            </form>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    )
}